import React, { useContext, useRef, useState } from 'react';
import { Avatar, Box, Divider, Stack, Typography } from '@mui/material';
import { UploadDraggingFile } from 'Images';
import { useDictionary, useIsDesktop } from 'Hooks';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { UploadFileButton } from '../../../Common/UploadFileButton';
import { AlternativeMedicineContext } from 'Context/AlternativeMedicine';

export const UploadFileArea = () => {
  const { handleFile } = useContext(AlternativeMedicineContext);
  const [isDragActive, setIsDragActive] = useState(false);

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();
  const fileInput = useRef<HTMLInputElement[]>([]);

  const onChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (file !== null) {
        handleFile(file);
        event.target.value = '';
      }
    }
  };

  const handleOnDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files[0];
    handleFile(droppedFiles);
    setIsDragActive(false);
  };

  const handleDragEnter = () => {
    setIsDragActive(true);
  };

  const handleDragLeave = () => {
    setIsDragActive(false);
  };

  const clickFileInput = (index: number) => {
    if (fileInput.current) {
      fileInput.current[index].click();
    }
  };

  return (
    <Stack
      sx={{
        backgroundColor: 'primary.shade_50',
        borderRadius: '16px',
        p: isDesktop ? '32px' : '20px 16px',
        mt: isDesktop ? undefined : '26px',
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            border: isDragActive ? '2px dashed #A020FF' : '1px solid #B7CCFF',
            padding: '24px',
            background: isDragActive ? '#FBFCFF' : '#fff',
            borderRadius: '8px',
            textAlign: 'center',
            position: 'relative',
          }}
        >
          <Box
            onDragOver={(event) => event.preventDefault()}
            onDrop={(e) => handleOnDrop(e)}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              bgcolor: 'transparent',
              top: 0,
              left: 0,
              zIndex: isDragActive ? 1 : undefined,
            }}
          />
          {isDragActive ? (
            <Stack padding='18px' mt='-1px'>
              <Avatar
                src={UploadDraggingFile}
                alt=''
                sx={{
                  width: '48px',
                  height: '48px',
                  margin: '12px auto',
                }}
              />
              <Typography
                sx={{ pointerEvents: 'none', color: '#344266' }}
                variant='body6'
              >
                {dictionary.dragFileHere}
              </Typography>
            </Stack>
          ) : (
            <Box>
              <Typography
                sx={{ pointerEvents: 'none', color: '#344266' }}
                variant='body3'
              >
                {dictionary.dragFileHere}
              </Typography>
              <Divider
                aria-hidden='true'
                sx={{
                  margin: '30px 0',
                  pointerEvents: 'none',
                  '&::before, &::after': {
                    borderColor: 'primary.shade_200',
                  },
                }}
              >
                {dictionary.or}
              </Divider>
              <input
                ref={(e) => {
                  fileInput.current[0] = e!;
                }}
                type='file'
                accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
                hidden
                onChange={(e) => onChangeHandler(e)}
                id={`upload-file`}
              />
              <label htmlFor={`upload-file`} className='sr-only'>
                {`${dictionary.uploadInvoice}`}
              </label>
              <UploadFileButton clickHandler={() => clickFileInput(0)}>
                {dictionary.uploadInvoice}
              </UploadFileButton>
            </Box>
          )}
        </Box>
      ) : (
        <Box>
          <Typography variant='body2'>{dictionary.gettingStarted1}</Typography>
          <input
            ref={(e) => {
              fileInput.current[0] = e!;
            }}
            type='file'
            accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
            hidden
            onChange={(e) => onChangeHandler(e)}
            id={`upload-file`}
          />
          <label htmlFor={`upload-file`} className='sr-only'>
            {`${dictionary.uploadFile}`}
          </label>

          <UploadFileButton clickHandler={() => clickFileInput(0)}>
            {dictionary.uploadFile}
          </UploadFileButton>
        </Box>
      )}
    </Stack>
  );
};
